import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { BlobPreloader } from '../components/BlobPreloader';
import { 
  Users, 
  Tag,
  Plus, 
  Edit2, 
  Trash2, 
  X, 
  Mail, 
  Phone, 
  User,
  Search,
  CheckCircle,
  Store,
  Upload,
  Download,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tab Component
const TabButton = ({ active, onClick, icon: Icon, label, count }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
      active
        ? 'border-[#2eb4ac] text-[#2eb4ac] bg-[#2eb4ac]/5'
        : 'border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-50'
    }`}
  >
    <Icon className="w-4 h-4" />
    {label}
    {count !== undefined && (
      <span className={`px-2 py-0.5 rounded-full text-xs ${
        active ? 'bg-[#2eb4ac]/10 text-[#2eb4ac]' : 'bg-slate-100 text-slate-600'
      }`}>
        {count}
      </span>
    )}
  </button>
);

// Agents Tab Component
const AgentsTab = ({ agents, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [searchQuery, setSearchQuery] = useState('');

  const filteredAgents = agents.filter(a => 
    a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAgent) {
        await axios.put(`${API}/agents/${editingAgent.id}`, formData);
        toast.success('Agent updated');
      } else {
        await axios.post(`${API}/agents`, formData);
        toast.success('Agent created');
      }
      onRefresh();
      resetForm();
    } catch (error) {
      toast.error('Failed to save agent');
    }
  };

  const handleEdit = (agent) => {
    setEditingAgent(agent);
    setFormData({ name: agent.name, email: agent.email, phone: agent.phone || '' });
    setShowForm(true);
  };

  const handleDelete = async (agentId) => {
    if (!window.confirm('Delete this agent?')) return;
    try {
      await axios.delete(`${API}/agents/${agentId}`);
      toast.success('Agent deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '' });
    setEditingAgent(null);
    setShowForm(false);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search agents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
          />
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#2eb4ac] text-white font-medium rounded-lg hover:bg-[#259790] transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Agent
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-slate-900">{editingAgent ? 'Edit Agent' : 'New Agent'}</h4>
            <button onClick={resetForm} className="p-1 hover:bg-slate-200 rounded">
              <X className="w-4 h-4" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="grid grid-cols-3 gap-3">
            <input
              type="text"
              required
              placeholder="Name *"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
            <input
              type="email"
              required
              placeholder="Email *"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
            <input
              type="tel"
              placeholder="Phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
            <div className="col-span-3 flex gap-2">
              <button type="submit" className="px-4 py-2 bg-[#2eb4ac] text-white text-sm font-medium rounded-lg">
                {editingAgent ? 'Update' : 'Create'}
              </button>
              <button type="button" onClick={resetForm} className="px-4 py-2 bg-slate-200 text-slate-700 text-sm font-medium rounded-lg">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Email</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Phone</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredAgents.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-4 py-8 text-center text-slate-500">
                  {searchQuery ? 'No agents match your search' : 'No agents yet'}
                </td>
              </tr>
            ) : (
              filteredAgents.map((agent) => (
                <tr key={agent.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#2eb4ac]/10 flex items-center justify-center">
                        <span className="text-sm font-semibold text-[#2eb4ac]">{agent.name.charAt(0)}</span>
                      </div>
                      <span className="font-medium text-slate-900">{agent.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{agent.email}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{agent.phone || '-'}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => handleEdit(agent)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-[#2eb4ac]">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(agent.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-slate-500 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Brands Tab Component
const BrandsTab = ({ brands, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [formData, setFormData] = useState({ name: '', category: '', description: '' });
  const [searchQuery, setSearchQuery] = useState('');

  const filteredBrands = brands.filter(b => 
    b.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (b.category && b.category.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBrand) {
        await axios.put(`${API}/brands/${editingBrand.id}`, formData);
        toast.success('Brand updated');
      } else {
        await axios.post(`${API}/brands`, formData);
        toast.success('Brand created');
      }
      onRefresh();
      resetForm();
    } catch (error) {
      toast.error('Failed to save brand');
    }
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setFormData({ name: brand.name, category: brand.category || '', description: brand.description || '' });
    setShowForm(true);
  };

  const handleDelete = async (brandId) => {
    if (!window.confirm('Delete this brand?')) return;
    try {
      await axios.delete(`${API}/brands/${brandId}`);
      toast.success('Brand deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', category: '', description: '' });
    setEditingBrand(null);
    setShowForm(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search brands..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
          />
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#2eb4ac] text-white font-medium rounded-lg hover:bg-[#259790] transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Brand
        </button>
      </div>

      {showForm && (
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-slate-900">{editingBrand ? 'Edit Brand' : 'New Brand'}</h4>
            <button onClick={resetForm} className="p-1 hover:bg-slate-200 rounded">
              <X className="w-4 h-4" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="grid grid-cols-3 gap-3">
            <input
              type="text"
              required
              placeholder="Brand Name *"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
            <input
              type="text"
              placeholder="Category"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
            <input
              type="text"
              placeholder="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
            <div className="col-span-3 flex gap-2">
              <button type="submit" className="px-4 py-2 bg-[#2eb4ac] text-white text-sm font-medium rounded-lg">
                {editingBrand ? 'Update' : 'Create'}
              </button>
              <button type="button" onClick={resetForm} className="px-4 py-2 bg-slate-200 text-slate-700 text-sm font-medium rounded-lg">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredBrands.length === 0 ? (
          <div className="col-span-full bg-white rounded-xl border border-slate-200 p-8 text-center">
            <Tag className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">{searchQuery ? 'No brands match your search' : 'No brands yet'}</p>
          </div>
        ) : (
          filteredBrands.map((brand) => (
            <div key={brand.id} className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-all">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                    <Tag className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">{brand.name}</h4>
                    {brand.category && <p className="text-xs text-slate-500">{brand.category}</p>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => handleEdit(brand)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-[#2eb4ac]">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(brand.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-slate-500 hover:text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {brand.description && (
                <p className="mt-3 text-sm text-slate-600 line-clamp-2">{brand.description}</p>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Sellers Tab Component
const SellersTab = ({ sellers, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingSeller, setEditingSeller] = useState(null);
  const [formData, setFormData] = useState({ seller_id: '', name: '', phone: '', store_name: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const filteredSellers = sellers.filter(s => 
    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.seller_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.store_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.phone?.includes(searchQuery)
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSeller) {
        await axios.put(`${API}/sellers/${editingSeller.id}`, formData);
        toast.success('Seller updated');
      } else {
        await axios.post(`${API}/sellers`, formData);
        toast.success('Seller created');
      }
      onRefresh();
      resetForm();
    } catch (error) {
      toast.error('Failed to save seller');
    }
  };

  const handleEdit = (seller) => {
    setEditingSeller(seller);
    setFormData({ 
      seller_id: seller.seller_id || '', 
      name: seller.name || '', 
      phone: seller.phone || '', 
      store_name: seller.store_name || '' 
    });
    setShowForm(true);
  };

  const handleDelete = async (sellerId) => {
    if (!window.confirm('Delete this seller?')) return;
    try {
      await axios.delete(`${API}/sellers/${sellerId}`);
      toast.success('Seller deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const resetForm = () => {
    setFormData({ seller_id: '', name: '', phone: '', store_name: '' });
    setEditingSeller(null);
    setShowForm(false);
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/sellers/export/csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sellers_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Sellers exported');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/sellers/template/csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'sellers_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/sellers/import`, formData);
      toast.success(`Imported ${response.data.imported} sellers`);
      if (response.data.errors?.length > 0) {
        toast.error(`${response.data.errors.length} rows had errors`);
      }
      onRefresh();
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4">
      {/* Header with actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search sellers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDownloadTemplate}
            className="flex items-center gap-2 px-3 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-200 transition-colors"
            title="Download sample template"
          >
            <FileText className="w-4 h-4" />
            Template
          </button>
          <label className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 text-sm font-medium rounded-lg hover:bg-blue-100 transition-colors cursor-pointer">
            <Upload className="w-4 h-4" />
            {importing ? 'Importing...' : 'Import'}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleImport}
              className="hidden"
              disabled={importing}
            />
          </label>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-3 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-200 transition-colors"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#2eb4ac] text-white font-medium rounded-lg hover:bg-[#259790] transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Seller
          </button>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-slate-900">{editingSeller ? 'Edit Seller' : 'New Seller'}</h4>
            <button onClick={resetForm} className="p-1 hover:bg-slate-200 rounded">
              <X className="w-4 h-4" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="grid grid-cols-4 gap-3">
            <input
              type="text"
              required
              placeholder="Seller ID *"
              value={formData.seller_id}
              onChange={(e) => setFormData({ ...formData, seller_id: e.target.value })}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
            <input
              type="text"
              required
              placeholder="Seller Name *"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
            <input
              type="tel"
              placeholder="Phone Number"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
            <input
              type="text"
              placeholder="Store Name"
              value={formData.store_name}
              onChange={(e) => setFormData({ ...formData, store_name: e.target.value })}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
            <div className="col-span-4 flex gap-2">
              <button type="submit" className="px-4 py-2 bg-[#2eb4ac] text-white text-sm font-medium rounded-lg">
                {editingSeller ? 'Update' : 'Create'}
              </button>
              <button type="button" onClick={resetForm} className="px-4 py-2 bg-slate-200 text-slate-700 text-sm font-medium rounded-lg">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Seller ID</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Seller Name</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Phone Number</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Store Name</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredSellers.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-4 py-8 text-center text-slate-500">
                  {searchQuery ? 'No sellers match your search' : 'No sellers yet. Add one or import from CSV.'}
                </td>
              </tr>
            ) : (
              filteredSellers.map((seller) => (
                <tr key={seller.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-slate-100 text-slate-700 text-xs font-mono rounded">{seller.seller_id}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center">
                        <span className="text-sm font-semibold text-orange-600">{seller.name?.charAt(0) || '?'}</span>
                      </div>
                      <span className="font-medium text-slate-900">{seller.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{seller.phone || '-'}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {seller.store_name ? (
                      <span className="flex items-center gap-1.5">
                        <Store className="w-3.5 h-3.5 text-slate-400" />
                        {seller.store_name}
                      </span>
                    ) : '-'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => handleEdit(seller)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-[#2eb4ac]">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(seller.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-slate-500 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Stats */}
      <div className="text-sm text-slate-500">
        Showing {filteredSellers.length} of {sellers.length} sellers
      </div>
    </div>
  );
};

// Main Settings Page
const Settings = () => {
  const [activeTab, setActiveTab] = useState('sellers');
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [brands, setBrands] = useState([]);
  const [sellers, setSellers] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [agentsRes, brandsRes, sellersRes] = await Promise.all([
        axios.get(`${API}/agents`),
        axios.get(`${API}/brands`).catch(() => ({ data: [] })),
        axios.get(`${API}/sellers`).catch(() => ({ data: [] }))
      ]);
      setAgents(agentsRes.data);
      setBrands(brandsRes.data);
      setSellers(sellersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <BlobPreloader size={88} label="Loading settings..." />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Settings</h1>
        <p className="text-slate-500 mt-1">Manage your master data and configurations</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="border-b border-slate-200 flex">
          <TabButton
            active={activeTab === 'sellers'}
            onClick={() => setActiveTab('sellers')}
            icon={Store}
            label="Sellers"
            count={sellers.length}
          />
          <TabButton
            active={activeTab === 'agents'}
            onClick={() => setActiveTab('agents')}
            icon={Users}
            label="Agents"
            count={agents.length}
          />
          <TabButton
            active={activeTab === 'brands'}
            onClick={() => setActiveTab('brands')}
            icon={Tag}
            label="Brands"
            count={brands.length}
          />
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'sellers' && <SellersTab sellers={sellers} onRefresh={fetchData} />}
          {activeTab === 'agents' && <AgentsTab agents={agents} onRefresh={fetchData} />}
          {activeTab === 'brands' && <BrandsTab brands={brands} onRefresh={fetchData} />}
        </div>
      </div>
    </div>
  );
};

export default Settings;
